import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { SupportCards } from '../components/SupportCards';

interface Page {
  slug: string;
  title: string;
  body: string;
  metaTitle: string | null;
  metaDescription: string | null;
  updatedAt: string;
}

/** The label above a page's title, by what kind of page it is. */
function eyebrowFor(slug: string): string {
  if (['privacy', 'terms', 'refund-policy', 'accessibility'].includes(slug))
    return 'Legal & policies';
  if (['about', 'contact'].includes(slug)) return 'Company';
  if (slug === 'getting-started') return 'Guides';
  return 'SkillXzen';
}

/**
 * Splits a stored page body into what the layout needs: the body without its
 * own top heading (the hero shows the title), and its section headings, given
 * ids so the "On this page" menu can link to them.
 */
function prepare(html: string): { body: string; sections: Array<{ id: string; title: string }> } {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  root.querySelector('h1')?.remove();
  const sections: Array<{ id: string; title: string }> = [];
  root.querySelectorAll('h2').forEach((heading, index) => {
    const title = heading.textContent?.trim() ?? '';
    const id = `s-${index + 1}-${title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')}`;
    heading.id = id;
    sections.push({ id, title });
  });
  return { body: root.innerHTML, sections };
}

/**
 * A published Site Content page — About, Privacy Policy, Terms and the rest —
 * in the help-centre layout: a hero with the title and summary, an "On this
 * page" menu built from the page's own headings, the text in a card, and the
 * support strip.
 *
 * Only pages inside their publishing window come back from the API; anything
 * else is a 404, shown as "not found" rather than a redirect.
 */
export function SitePage() {
  const { slug = '' } = useParams();
  const [page, setPage] = useState<Page | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setMissing(false);
    setFailed(null);
    api<Page>(`/site/pages/${encodeURIComponent(slug)}`)
      .then((result) => {
        if (!cancelled) setPage(result);
      })
      .catch((caught: ApiError) => {
        if (cancelled) return;
        // Only a 404 means the page does not exist; anything else is a fault
        // worth saying so about, not a missing page.
        if (caught.status === 404) setMissing(true);
        else setFailed(caught.message || 'This page could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // The tab title and description follow the page, as its SEO fields intend.
  useEffect(() => {
    if (!page) return;
    const previous = document.title;
    document.title = page.metaTitle || `${page.title} — SkillXzen`;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content') ?? null;
    if (meta && page.metaDescription) meta.setAttribute('content', page.metaDescription);
    return () => {
      document.title = previous;
      if (meta && previousDescription !== null) meta.setAttribute('content', previousDescription);
    };
  }, [page]);

  // Sanitised by the API, on save and again when served.
  const prepared = useMemo(() => (page ? prepare(page.body) : null), [page]);

  // Highlight the section nearest the top of the screen.
  useEffect(() => {
    if (!prepared || prepared.sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setCurrent(visible[0].target.id);
      },
      { rootMargin: '-90px 0px -60% 0px' },
    );
    prepared.sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [prepared]);

  if (missing || failed) {
    return (
      <div className="wrap">
        <div className="site-page">
          {missing ? (
            <div className="state">
              <div className="state__art" aria-hidden="true">
                ◎
              </div>
              <h4>Page not found</h4>
              <p>This page does not exist or is no longer published.</p>
              <Link className="btn btn--sm" to="/catalog">
                Browse the catalog
              </Link>
            </div>
          ) : (
            <div className="empty">{failed}</div>
          )}
        </div>
      </div>
    );
  }

  const updated = page
    ? new Date(page.updatedAt).toLocaleDateString(undefined, { dateStyle: 'long' })
    : null;

  return (
    <>
      <section className="cat__hero helphero helphero--page">
        <div className="wrap helphero__inner">
          <p className="helphero__eyebrow">{eyebrowFor(slug)}</p>
          <h1 className="helphero__title">{page?.title ?? ' '}</h1>
          {page?.metaDescription ? <p className="helphero__lede">{page.metaDescription}</p> : null}
          {updated ? <p className="helphero__meta">Last updated {updated}</p> : null}
        </div>
      </section>

      <div className="wrap">
        <div
          className={`helpbody${prepared && prepared.sections.length > 1 ? '' : ' helpbody--single'}`}
        >
          {prepared && prepared.sections.length > 1 ? (
            <aside className="helpnav" aria-label="On this page">
              <p className="helpnav__label">On this page</p>
              {prepared.sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={`helpnav__link${current === section.id ? ' is-current' : ''}`}
                  aria-current={current === section.id ? 'true' : undefined}
                >
                  <span>{section.title}</span>
                </a>
              ))}
            </aside>
          ) : null}

          <div className="helpmain">
            {!prepared ? (
              <div className="skel" style={{ height: 420 }} />
            ) : (
              <article
                className="prose site-page__body pagecard"
                dangerouslySetInnerHTML={{ __html: prepared.body }}
              />
            )}
            <SupportCards current={`/pages/${slug}`} />
          </div>
        </div>
      </div>
    </>
  );
}
